import React, { memo, useEffect, useState } from 'react';
import { AuthLayout } from 'layouts/AuthLayout';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Spinner } from 'reactstrap';
import Api from 'services/api';

const Footer = memo(() => (
    <div className='center_wrapper'>
        <p className="para3">Need help? <Link to="/login">Contact Support</Link></p>
    </div>
));

export default function VerifyEmail() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const token = searchParams.get('token');
    const email = searchParams.get('email');

    const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'already_verified' | 'error'
    const [message, setMessage] = useState('');
    const [resending, setResending] = useState(false);

    useEffect(() => {
        if (!token || !email) {
            setStatus('error');
            setMessage('Invalid or missing verification parameters.');
            return;
        }

        let isMounted = true;
        const doVerify = async () => {
            try {
                const response = await Api.verifyEmail({ token, email });
                if (!isMounted) return;

                if (response?.data?.success) {
                    if (response?.data?.already_verified) {
                        setStatus('already_verified');
                    } else {
                        setStatus('success');
                    }
                    setMessage(response?.data?.message || 'Email verified successfully.');
                } else {
                    setStatus('error');
                    setMessage(response?.data?.message || 'Verification link is invalid or has expired.');
                }
            } catch (err) {
                if (!isMounted) return;
                setStatus('error');
                setMessage(err?.response?.data?.message || 'Verification failed. Please try again.');
            }
        };

        doVerify();

        return () => {
            isMounted = false;
        };
    }, [token, email]);

    const handleResend = async () => {
        if (!email) {
            navigate('/verify-email-pending');
            return;
        }

        setResending(true);
        try {
            const response = await Api.resendVerification({ email });
            if (response?.data?.success) {
                toast.success(response?.data?.message || 'New verification email sent successfully!');
                navigate('/verify-email-pending', { state: { email } });
            } else {
                toast.error(response?.data?.message || 'Failed to send verification link.');
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Error sending verification link.');
        } finally {
            setResending(false);
        }
    };

    return (
        <AuthLayout
            heading="Email Verification"
            title="Activating your Maya Bridge agency vendor account."
            footer={<Footer />}
        >
            <div className='auth_form' style={{ textAlign: 'center', padding: '10px 0' }}>
                {status === 'verifying' && (
                    <div style={{ padding: '30px 10px' }}>
                        <Spinner color="light" style={{ width: '3.5rem', height: '3.5rem' }} />
                        <h4 style={{ color: '#ffffff', marginTop: '20px', fontWeight: '600' }}>
                            Verifying your email...
                        </h4>
                        <p style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '14px' }}>
                            Please wait while we confirm your agency vendor account credentials.
                        </p>
                    </div>
                )}

                {(status === 'success' || status === 'already_verified') && (
                    <div style={{ padding: '20px 10px' }}>
                        <div style={{
                            width: '70px',
                            height: '70px',
                            borderRadius: '50%',
                            backgroundColor: '#e3f9e5',
                            border: '2px solid #38a169',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px auto'
                        }}>
                            <i className="fa-solid fa-check" style={{ color: '#2f855a', fontSize: '32px' }}></i>
                        </div>
                        <h4 style={{ color: '#ffffff', fontWeight: '700', marginBottom: '10px' }}>
                            {status === 'already_verified' ? 'Already Verified!' : 'Verification Successful!'}
                        </h4>
                        <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '15px', lineHeight: '1.6', marginBottom: '25px' }}>
                            {message || 'Your email has been verified. You can now log in to Maya Bridge.'}
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/login')}
                            className="btn_auth"
                            style={{ cursor: 'pointer', width: '100%' }}
                        >
                            <span>
                                <i className="fa-solid fa-right-to-bracket me-2"></i>
                                Proceed to Login
                            </span>
                        </button>
                    </div>
                )}

                {status === 'error' && (
                    <div style={{ padding: '20px 10px' }}>
                        <div style={{
                            width: '70px',
                            height: '70px',
                            borderRadius: '50%',
                            backgroundColor: '#fee2e2',
                            border: '2px solid #ef4444',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px auto'
                        }}>
                            <i className="fa-solid fa-xmark" style={{ color: '#dc2626', fontSize: '34px' }}></i>
                        </div>
                        <h4 style={{ color: '#ff6b6b', fontWeight: '700', marginBottom: '10px' }}>
                            Verification Failed
                        </h4>
                        <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '15px', lineHeight: '1.6', marginBottom: '25px' }}>
                            {message}
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <button
                                type="button"
                                onClick={handleResend}
                                disabled={resending}
                                className={`btn_auth ${resending ? "disabled-button" : ""}`}
                                style={{ cursor: 'pointer', width: '100%' }}
                            >
                                {resending ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        <span>Sending new link...</span>
                                    </>
                                ) : (
                                    <span>
                                        <i className="fa-solid fa-paper-plane me-2"></i>
                                        Send New Verification Link
                                    </span>
                                )}
                            </button>

                            <Link to="/login" style={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: '600', textDecoration: 'none', fontSize: '14px', marginTop: '10px' }}>
                                <i className="fa-solid fa-arrow-left me-2"></i>
                                Back to Login
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </AuthLayout>
    );
}
